// Tells the caller when a topic has become started for the reader between one snapshot and the
// next, and returns that topic's key on the render that first sees the change.
//
// The first snapshot is only remembered, so a reload or first load onto a plan whose topics
// are already started reports nothing, and a change of plan starts over the same way. The previous snapshot is held whatever page the reader
// is on, so only a change seen by this screen counts.

import { useEffect, useRef } from 'react';

import type { PlanSnapshot } from './snapshot.js';

export function useStartedTopic(
  snapshot: PlanSnapshot | null,
  onStarted: (topicKey: string) => void,
): string | null {
  const previous = useRef<ReadonlySet<string> | null>(null);
  // Read during render, before the effect below records this snapshot, so the caller can
  // tell on the very render that sees the change.
  const newlyStarted =
    snapshot === null || previous.current === null
      ? null
      : (snapshot.topics.find(
          (topic) => topic.viewer.started && !(previous.current?.has(topic.key) ?? false),
        )?.key ?? null);

  useEffect(() => {
    if (snapshot === null) {
      previous.current = null;
      return;
    }
    const before = previous.current;
    previous.current = new Set(
      snapshot.topics.filter((topic) => topic.viewer.started).map((topic) => topic.key),
    );
    if (before === null) {
      return;
    }
    const started = snapshot.topics.find(
      (topic) => topic.viewer.started && !before.has(topic.key),
    );
    if (started !== undefined) {
      onStarted(started.key);
    }
    // `onStarted` is read when the snapshot changes; only the snapshot drives this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot]);
  return newlyStarted;
}
